import { Controller } from "@hotwired/stimulus";
import { CloudinaryFile } from "../../types/cloudinary_custom_types";

interface ExistingAttachment {
  id: number;
  serialized_file: {
    thumbnail_url: string;
    resource_type?: string;
    url?: string;
  };
}

interface ThumbnailOptions {
  url: string;
  dataset?: Record<string, string>;
  inputs?: Record<string, string | number>;
  isVideo?: boolean;
}

/**
 * Base Stimulus controller for handling local media input with thumbnail display.
 *
 * This controller manages file uploads by creating thumbnail previews and hidden form inputs.
 * It supports both new file uploads and existing media attachments.
 *
 * @example
 * ```html
 * <div data-controller="media-input--local"
 *      data-media-input--local-input-name-value="content_media_attributes"
 *      data-media-input--local-existing-value="<%= @existing_media.to_json %>">
 *   <div data-media-input--local-target="thumbnails"></div>
 * </div>
 * ```
 *
 * @remarks
 * - Automatically loads existing media on connect
 * - Creates thumbnail previews with remove buttons
 * - Generates hidden form inputs for Rails form submission
 * - Supports soft deletion of existing attachments via _destroy flag
 */

export default class extends Controller<HTMLElement> {
  static targets = ["thumbnails"];

  static values = {
    existing: Array,
    inputName: String,
  };

  /** Target element where thumbnails are displayed */
  declare readonly thumbnailsTarget: HTMLElement;
  /** Whether thumbnails target exists */
  declare readonly hasThumbnailsTarget: boolean;
  /** Array of existing media attachments */
  declare readonly existingValue: ExistingAttachment[];
  /** Whether existing value is present */
  declare readonly hasExistingValue: boolean;
  /** Base name for form inputs */
  declare readonly inputNameValue: string;
  /** Internal counter for generating unique local IDs */
  private lastLocalId: number = 0;

  connect(): void {
    this.loadExistingMedia();
  }

  /**
   * Handles new file uploads by creating thumbnail previews.
   *
   * @param file - The uploaded file object from cloudinary
   * @returns Promise resolving to true if file was handled successfully
   */
  async onFileAdded(file: CloudinaryFile): Promise<boolean> {
    const medium = file.identifier + "#" + file.signature;
    const thumbnail = this.createThumbnail({
      url: file.url,
      inputs: { medium, kind: "upload" },
      isVideo: this.isVideo(file),
    });

    this.thumbnailsTarget.appendChild(thumbnail);

    return true;
  }

  /**
   * Removes a thumbnail when the remove button is clicked.
   * For existing attachments, marks them for destruction instead of removing.
   *
   * @param event - Click event from the remove button
   */
  remove(event: Event): void {
    const target = event.target as HTMLElement;
    const thumbnail = target.closest("[data-local-id]") as HTMLElement;

    if (confirm("Supprimer cette image ?")) {
      if (thumbnail.dataset.id) {
        this.markForDestruction(thumbnail);
      } else {
        thumbnail.remove();
      }
    }
  }

  /**
   * Loads existing media attachments and creates thumbnails for them.
   * Called automatically on controller connect.
   *
   * @private
   */
  private loadExistingMedia(): void {
    if (this.hasExistingValue) {
      this.existingValue.forEach((attachment) => {
        const isVideo =
          attachment.serialized_file.resource_type === "video" ||
          this.isVideo(attachment.serialized_file.thumbnail_url);

        const thumbnail = this.createThumbnail({
          url: attachment.serialized_file.thumbnail_url,
          inputs: { id: attachment.id },
          isVideo,
        });

        this.thumbnailsTarget.appendChild(thumbnail);
      });
    }
  }

  /**
   * Generates a unique local ID for new thumbnails
   *
   * @protected
   */
  protected get lastId(): number {
    return (this.lastLocalId = (this.lastLocalId || 0) + 1);
  }

  /**
   * Creates a thumbnail element with associated hidden inputs.
   *
   * @param options - Configuration for the thumbnail
   * @returns The created thumbnail DOM element
   *
   * @protected
   */
  protected createThumbnail({
    url,
    dataset = {},
    inputs = {},
    isVideo = false,
  }: ThumbnailOptions): HTMLElement {
    const localId = this.lastId;
    const thumbnail = this.thumbnailElement(url, isVideo);
    Object.assign(thumbnail.dataset, {
      ...dataset,
      localId: localId.toString(),
      ...inputs,
    });
    Object.entries(inputs)
      .map(([k, v]) => this.createHiddenInput(`[${localId}][${k}]`, v.toString()))
      .map((input) => thumbnail.appendChild(input));
    return thumbnail;
  }

  /**
   * Creates the thumbnail DOM element with image and remove button.
   *
   * @param url - URL for the thumbnail image
   * @returns The created thumbnail element
   *
   * @protected
   */
  protected thumbnailElement(url: string, isVideo: boolean = false): HTMLElement {
    const thumbnail = document.createElement("div");
    thumbnail.className = "relative group w-20 h-20 bg-gray-100 rounded-lg overflow-hidden";
    const thumbUrl = isVideo ? this.videoThumbnailUrl(url) : url;
    const mediaTag = `<img src="${thumbUrl}" class="w-full h-full object-cover">`;

    thumbnail.innerHTML = `
    ${mediaTag}
    <button
      type="button"
      data-action="click->${this.identifier}#remove"
      class="absolute w-3 h-3 text-brand_main transition ease-in-out bg-white rounded-full top-2 right-2 hover:text-white hover:bg-brand_darker hover:scale-125"
    >
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
      </svg>
    </button>
  `;
    return thumbnail;
  }

  /**
   * Creates a hidden input element for form submission.
   *
   * @param name - Input name (will be prefixed with inputNameValue)
   * @param value - Input value
   * @returns The created input element
   *
   * @protected
   */
  protected createHiddenInput(name: string, value: string): HTMLInputElement {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = `${this.inputNameValue}${name}`;
    input.value = value;
    return input;
  }

  /**
   * Marks an existing attachment for destruction by adding a _destroy input.
   * The thumbnail is hidden but not removed from the DOM.
   *
   * @param thumbnail - The thumbnail element to mark for destruction
   *
   * @private
   */
  private markForDestruction(thumbnail: HTMLElement): void {
    const localId = thumbnail.dataset.localId!;
    const destroyInput = this.createHiddenInput(`[${localId}][_destroy]`, "1");
    thumbnail.appendChild(destroyInput);
    thumbnail.style.display = "none";
  }
  protected videoThumbnailUrl(url: string): string {
    // Cloudinary: replace /video/upload/ with /video/upload/so_0,f_jpg/ to get first frame as image
    return url
      .replace(/\/video\/upload\//, "/video/upload/so_0,c_fill,w_80,h_80/")
      .replace(/\.(mp4|mov|avi|webm|flv|mkv)$/, ".jpg");
  }

  private isVideo(fileOrUrl: CloudinaryFile | string): boolean {
    if (typeof fileOrUrl === "string") {
      return (
        /\/video\/upload\//.test(fileOrUrl) || /\.(mp4|mov|avi|webm|flv|mkv)(\?|$)/i.test(fileOrUrl)
      );
    }
    // CloudinaryFile : Cloudinary expose resource_type
    if ((fileOrUrl as any).resource_type === "video") return true;
    return this.isVideo(fileOrUrl.url);
  }
}
